import {
  ApprovalResults,
  ApprovalStatusDistributionChartFiltersDto,
  ResponseCount,
} from './../../../../interfaces/reports/approval-status-distribution-chart';
import {
  Component,
  EventEmitter,
  Input,
  InputSignal,
  Output,
  ViewChild,
  effect,
  input,
} from '@angular/core';
import {
  ApexAxisChartSeries,
  ApexChart,
  ChartComponent,
  ApexDataLabels,
  ApexPlotOptions,
  ApexResponsive,
  ApexXAxis,
  ApexLegend,
  ApexFill,
} from 'ng-apexcharts';
import { NgApexchartsModule } from 'ng-apexcharts';
import { SpinnerComponent } from '../../../spinner/spinner.component';
import { SelectModule } from 'primeng/select';
import { ApprovalStatusDistributionChart } from '../../../../interfaces/reports/approval-status-distribution-chart';
import { FormsModule } from '@angular/forms';
import { NgClass, TitleCasePipe } from '@angular/common';
import { BaseDashboardChartComponent } from '../../base-dashboard-chart.component';
import { ChartCardComponent } from "../../chart-card/chart-card.component";
import { Subscription } from 'rxjs';
import {TranslatePipe} from "@ngx-translate/core";
import { TranslateService } from '@ngx-translate/core';
export type ChartOptionsStacked100 = {
  series: ApexAxisChartSeries;
  chart: ApexChart;
  dataLabels: ApexDataLabels;
  plotOptions: ApexPlotOptions;
  responsive: ApexResponsive[];
  xaxis: ApexXAxis;
  yaxis: ApexYAxis | ApexYAxis[];
  legend: ApexLegend;
  fill: ApexFill;
};

@Component({
  selector: 'co-stacked-columns-100',
  imports: [NgApexchartsModule, SelectModule, FormsModule, ChartCardComponent, SpinnerComponent, TranslatePipe],
  templateUrl: './stacked-columns-100.component.html',
  styleUrl: './stacked-columns-100.component.css',
})
export class StackedColumns100Component extends BaseDashboardChartComponent {

  @ViewChild('chart') chart?: ChartComponent;
  public chartOptions: Partial<ChartOptionsStacked100> | any;
  data: InputSignal<ApprovalStatusDistributionChart> = input.required();
  results: InputSignal<ApprovalResults[]> = input.required();
  chartReady = input.required<boolean>();


  message: string | undefined;
  pValue: number | string | undefined;
  statistic: number | string | undefined;
  private langChangeSub?: Subscription;
  constructor(private translate: TranslateService) {
    super();
     this.langChangeSub = this.translate.onLangChange.subscribe(() => {
       const current = this.data().currentChartData;
      const historical = this.data().historicalChartData;

      if (!current || !historical) return;

      this.chartOptions = this.createChartOptions(current, historical); 
  });
    effect(() => {

      const res = this.results();
      this.pValue = res[0]?.pValue ?? 0;
      this.statistic = res[0]?.statistic ?? 0;
      if (this.pValue < 0.05) {
        this.message = "Reject H0 - Approval proportions differ between groups."
      } else {
        this.message = "Fail to reject H0 - Proportions stable between groups."
      }

      const current = this.data().currentChartData;
      const historical = this.data().historicalChartData;

      if (!current || !historical) return;

      this.chartOptions = this.createChartOptions(current, historical);
    });
  }

  ngOnDestroy() {
      this.langChangeSub?.unsubscribe();
  }
  
  createChartOptions(current: ApprovalStatusDistributionChart['currentChartData'], historical: ApprovalStatusDistributionChart['historicalChartData']): any {

    const allGroups = new Set([
      ...current.map(item => item.group),
      ...historical.map(item => item.group)
    ]);
    const groupNames = Array.from(allGroups);


    const hasHistoricalData = historical.length > 0;
    const categories = hasHistoricalData
      ? groupNames.flatMap(g => [[g, this.translate.instant('STACKED_COLUMNS100.CURRENT')], [g, this.translate.instant('STACKED_COLUMNS100.HISTORICAL')]])
      : groupNames.map(g => [g]);


    const getCount = (data: any[], group: string, response: string): number => {
      const item = data.find(d => d.group === group);
      if (item) {
        return item.responseCounts.find((rc: any) => rc.response === response)?.count ?? 0;
      }
      return 0;
    };


    const responses = ['Covered', 'Not Covered', 'Reservation of Rights'];

    const getRoundedPercentages = (data: any[], group: string): number[] => {
      const counts = responses.map(response => getCount(data, group, response));
      const total = counts.reduce((sum, count) => sum + count, 0);

      if (total === 0) {
        return counts;
      }

      const exactPercentages = counts.map(count => count * 100 / total);
      const roundedPercentages = exactPercentages.map(value => Math.floor(value));
      let remainder = 100 - roundedPercentages.reduce((sum, value) => sum + value, 0);

      exactPercentages
        .map((value, index) => ({ index, fraction: value - roundedPercentages[index] }))
        .sort((left, right) => right.fraction - left.fraction)
        .slice(0, remainder)
        .forEach(({ index }) => roundedPercentages[index]++);

      return roundedPercentages;
    };

    const getSeriesData = (response: string, dataType: 'current' | 'historical'): number[] => {
      const data = dataType === 'current' ? current : historical;
      const responseIndex = responses.indexOf(response);
      return groupNames.flatMap(group => {
        const percentage = getRoundedPercentages(data, group)[responseIndex];
        if (hasHistoricalData) {
          if (dataType === 'current') {
            return [percentage, 0];
          } else {
            return [0, percentage];
          }
        } else {
          return [percentage];
        }
      });
    };
    const colors = [
      '#155b8c', // Approved - Historical 
      '#FF2800', // Rejected - Historical 
      '#ffbb78', // With Reservation - Historical 
      '#4faef0', // Approved - Historical (muted)
      '#ff9896', // Rejected - Historical (muted)
      '#ffbb78', // With Reservation - Historical (muted)
    ];
  const series = [
  {
    // Only the label is translated — data still uses Greek source values
    name: `${this.translate.instant('STACKED_COLUMNS100.APPROVED')} - ${this.translate.instant('STACKED_COLUMNS100.CURRENT')}`,
    stack: 'current',
    data: getSeriesData('Covered', 'current'),
  },
  {
    name: `${this.translate.instant('STACKED_COLUMNS100.REJECTED')} - ${this.translate.instant('STACKED_COLUMNS100.CURRENT')}`,
    stack: 'current',
    data: getSeriesData('Not Covered', 'current'),
  },
  {
    name: `${this.translate.instant('STACKED_COLUMNS100.WITH_RESERVATION')} - ${this.translate.instant('STACKED_COLUMNS100.CURRENT')}`,
    stack: 'current',
    data: getSeriesData('Reservation of Rights', 'current'),
  },
];

if (hasHistoricalData) {
  series.push(
    {
      name: `${this.translate.instant('STACKED_COLUMNS100.APPROVED')} - ${this.translate.instant('STACKED_COLUMNS100.HISTORICAL')}`,
      stack: 'historical',
      data: getSeriesData('Covered', 'historical'),
    },
    {
      name: `${this.translate.instant('STACKED_COLUMNS100.REJECTED')} - ${this.translate.instant('STACKED_COLUMNS100.HISTORICAL')}`,
      stack: 'historical',
      data: getSeriesData('Not Covered', 'historical'),
    },
    {
      name: `${this.translate.instant('STACKED_COLUMNS100.WITH_RESERVATION')} - ${this.translate.instant('STACKED_COLUMNS100.HISTORICAL')}`,
      stack: 'historical',
      data: getSeriesData('Reservation of Rights', 'historical'),
    }
  );
}


    return {
      series: series,
      chart: {
        type: 'bar',
        height: 500,
        stacked: true,
        stackType: '100%',
        toolbar: { show: true },
      },
      plotOptions: {
        bar: {
          horizontal: false,

        },
      },
      colors: colors,
      xaxis: {
        categories: categories,
        title: {
          text: '',
          align: 'center',
        },
        labels: {
          rotate: -45,
          hideOverlappingLabels: false,
          trim: false,
          style: {
            fontSize: '10px',
            fontFamily: 'Arial, sans-serif',
            colors: '#333'
          },
          formatter: (val: string) => val || 'N/A'
        }
      },

      yaxis: {
        show: true,
        title: {
          text: this.translate.instant('STACKED_COLUMNS100.PERCENTAGE_OF_CLAIMS'),
        },
      },
      legend: {
        show: true,
        position: "bottom",
        offsetX: 0,
        offsetY: 20
      },
      responsive: [
        {
          breakpoint: 480,
          options: {
            legend: {
              position: 'bottom',
              offsetX: -10,
              offsetY: 0,
            },
          },
        },
      ],
      fill: {
        opacity: 1
      }
    };

  }
  override onDropdownChange() {
    this.onFilterChange.emit(this.selectedFilters)
  }

  get hasChartData(): boolean {
    return (
      (this.data().currentChartData?.some(item => item.responseCounts?.length > 0)) ||
      (this.data().historicalChartData?.some(item => item.responseCounts?.length > 0))
    );
  }

}
